import {
  Component,
  ChangeDetectionStrategy,
  OnChanges,
  OnDestroy,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { ChartTypes } from '@mode/shared/contract-common';
import { Subscription } from 'rxjs';

const RANGE_DEFAULTS = {
  isIndependent: false,
  min: '',
  max: '',
} as const;

@Component({
  selector: 'mode-format-builder-range',
  templateUrl: './range.component.html',
  styleUrls: ['./range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeComponent implements OnChanges, OnDestroy {
  @Input() range!: ChartTypes.RangeFormat;
  @Output() update = new EventEmitter<ChartTypes.RangeFormat>();

  rangeForm = this.fb.group({
    isIndependent: [false],
    min: ['', { updateOn: 'blur' }],
    max: ['', { updateOn: 'blur' }],
  });

  subscription: Subscription;

  constructor(private fb: UntypedFormBuilder) {
    this.subscription = this.rangeForm.valueChanges.subscribe((rangeFormat: ChartTypes.RangeFormat) => {
      if (rangeFormat && !rangeFormat.isIndependent && this.range && this.range.isIndependent) {
        // When the 'Use independent axis range' checkbox is unchecked, we need to restore the previous
        // values. Those values are available in the previously input RangeFormat.
        rangeFormat.min = this.range.min;
        rangeFormat.max = this.range.max;
      }
      this.update.emit(rangeFormat);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.range) {
      if (this.range?.isIndependent && this.rangeForm.get('min')?.enabled) {
        this.rangeForm.get('min')?.disable({ emitEvent: false });
        this.rangeForm.get('max')?.disable({ emitEvent: false });
      } else if (!this.range?.isIndependent && this.rangeForm.get('min')?.disabled) {
        this.rangeForm.get('min')?.enable({ emitEvent: false });
        this.rangeForm.get('max')?.enable({ emitEvent: false });
      }
      // If the 'Use independent axis range' is selected, the min/max UI inputs need to
      // be cleared/empty. However, when the checkbox is unchecked, we need to show
      // the previously set values. By clearing the values in the patchValue, it won't
      // trigger a 'valueChanges' event and the min/max in the store won't be cleared
      // so we can restore them later.
      const patchValue = changes.range.currentValue?.isIndependent
        ? { ...changes.range.currentValue, min: undefined, max: undefined }
        : { ...changes.range.currentValue };
      this.rangeForm.patchValue(
        { ...RANGE_DEFAULTS, ...patchValue },
        {
          emitEvent: false,
        }
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
